import { Container, Text, TextStyle, Application, Graphics, FederatedPointerEvent } from 'pixi.js';
import { PlacementMode, PlacementResult, WallBuildMode } from '@gameplay/building/PlacementSystem';
import { RoomDefinition } from '@gameplay/management/rooms/RoomTypes';
import { RoomUI, RoomUICallbacks } from './RoomUI';
import { FurnitureUI, FurnitureUICallbacks } from './FurnitureUI';

type HistorySource = 'normal' | 'undo' | 'redo';

export class HUD extends Container {
  private modeLabel: Text;
  private statusLabel: Text;
  private roomLabel: Text;
  private controlsLabel!: Text;
  private roomUI: RoomUI;
  private furnitureUI: FurnitureUI;
  private hasPlacementFeedback = false;
  private app: Application;
  private wallModeButton!: Container;
  private wallModeIcon!: Graphics;
  private wallModeLabel!: Text;
  private wallModeToggleCallback?: () => WallBuildMode;

  constructor(roomUICallbacks: RoomUICallbacks, furnitureUICallbacks: FurnitureUICallbacks, app: Application) {
    super();
    
    this.app = app;

    const modeStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xffffff,
      stroke: '#000000',
      strokeThickness: 4
    });

    const statusStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
      stroke: '#000000',
      strokeThickness: 3
    });

    this.modeLabel = new Text('', modeStyle);
    this.modeLabel.position.set(16, 16);
    this.addChild(this.modeLabel);

    this.statusLabel = new Text('', statusStyle);
    this.statusLabel.position.set(16, 48);
    this.addChild(this.statusLabel);

    this.roomLabel = new Text('', statusStyle);
    this.roomLabel.position.set(16, 76);
    this.addChild(this.roomLabel);

    this.controlsLabel = new Text('', statusStyle);
    this.controlsLabel.position.set(16, 104);
    this.addChild(this.controlsLabel);

    // Create wall mode toggle button
    this.createWallModeButton();

    // Initialize Room UI
    this.roomUI = new RoomUI(roomUICallbacks);
    // Position on the right side of the game screen with margin
    const rightMargin = 20;
    const roomUIWidth = 280; // Match PANEL_WIDTH from RoomUI
    this.roomUI.position.set(this.app.screen.width - roomUIWidth - rightMargin, 20);
    this.addChild(this.roomUI);

    // Initialize Furniture UI
    this.furnitureUI = new FurnitureUI(furnitureUICallbacks);
    // Position to the left of the Room UI
    const furnitureUIWidth = 280; // Match PANEL_WIDTH from FurnitureUI
    const panelSpacing = 20;
    this.furnitureUI.position.set(
      this.app.screen.width - roomUIWidth - furnitureUIWidth - rightMargin - panelSpacing, 
      20
    );
    this.addChild(this.furnitureUI);

    this.setPlacementMode('floor');
  }

  public updatePlacement(result: PlacementResult): void {
    this.hasPlacementFeedback = true;

    const source: HistorySource = result.source ?? 'normal';
    const prefix = this.getHistoryPrefix(source);
    const verb = this.getPlacementVerb(source);

    if (result.mode === 'floor') {
      const placed = result.floors.length;
      const rejected = result.rejectedTiles.length;
      const placedLabel = this.formatCount(placed, 'floor tile');
      const rejectedLabel = rejected > 0 ? `, blocked ${rejected}` : '';
      this.statusLabel.text = `${prefix}${verb} ${placedLabel}${rejectedLabel}.`;
    } else if (result.mode === 'door') {
      const placed = result.rejectedTiles.length === 0 ? 1 : 0;
      const rejected = result.rejectedTiles.length;
      const placedLabel = this.formatCount(placed, 'door');
      const rejectedLabel = rejected > 0 ? `, no adjacent walls found` : '';
      this.statusLabel.text = `${prefix}${verb} ${placedLabel}${rejectedLabel}.`;
    } else if (result.mode === 'furniture') {
      const placed = result.furniture.length;
      const rejected = result.rejectedTiles.length;
      const placedLabel = this.formatCount(placed, 'furniture item');
      const rejectedLabel = rejected > 0 ? `, placement failed` : '';
      this.statusLabel.text = `${prefix}${verb} ${placedLabel}${rejectedLabel}.`;
    } else {
      const placed = result.walls.length;
      const rejected = result.rejectedWalls.length;
      const placedLabel = this.formatCount(placed, 'wall segment');
      const rejectedLabel = rejected > 0 ? `, blocked ${rejected}` : '';
      this.statusLabel.text = `${prefix}${verb} ${placedLabel}${rejectedLabel}.`;
    }
  }

  public setPlacementMode(mode: PlacementMode): void {
    let label: string;
    let hint: string;
    
    if (mode === 'floor') {
      label = 'Floor Tiles';
      hint = 'Press 2 for walls, 3 for furniture, 4 for doors, 5 for room manager';
    } else if (mode === 'wall') {
      label = 'Wall Perimeter';
      hint = 'Press 1 for floors, 3 for furniture, 4 for doors, 5 for room manager';
    } else if (mode === 'door') {
      label = 'Doors';
      hint = 'Press 1 for floors, 2 for walls, 3 for furniture, 5 for room manager. Press R to rotate door direction';
    } else if (mode === 'room_manager') {
      label = 'Room Manager';
      hint = 'Use Room Manager panel to categorize rooms. Press 1-4 to return to building modes';
    } else {
      label = 'Furniture';
      hint = 'Press 1 for floors, 2 for walls, 4 for doors, 5 for room manager. Use F for Furniture UI. Press R to rotate';
    }
    
    this.modeLabel.text = `Mode: ${label} (${hint})`;
    this.hasPlacementFeedback = false;
    this.statusLabel.text = this.getInstructionText(mode);
    
    // Show/hide wall mode button based on current mode
    if (this.wallModeButton) {
      this.wallModeButton.visible = (mode === 'wall');
    }
    
    // Auto-show/hide Room UI based on mode
    if (mode === 'room_manager') {
      this.showRoomUI();
      this.roomLabel.text = 'Room Manager mode active. Use panel to categorize rooms.';
    } else {
      this.hideRoomUI();
      this.roomLabel.text = 'Press 5 for Room Manager to categorize rooms.';
    }
  }

  public showRemoval(mode: PlacementMode, context: HistorySource = 'normal', count = 1): void {
    this.hasPlacementFeedback = true;
    const prefix = this.getHistoryPrefix(context);
    let noun: string;
    
    if (mode === 'floor') {
      noun = 'floor tile';
    } else if (mode === 'door') {
      noun = 'door';
    } else if (mode === 'furniture') {
      noun = 'furniture item';
    } else {
      noun = 'wall segment';
    }
    
    const verb = this.getRemovalVerb(context);
    this.statusLabel.text = `${prefix}${verb} ${this.formatCount(count, noun)}.`;
  }

  public showRoomQuality(room: RoomDefinition | undefined): void {
    if (!room) {
      this.roomLabel.text = 'Press 1-6 to tag the room beneath the cursor.';
      return;
    }

    const percentage = Math.round(room.quality * 100);
    const label = this.getCategoryLabel(room.category);
    this.roomLabel.text = `${label}: ${percentage}% proximity`;
  }

  private getCategoryLabel(category: RoomDefinition['category']): string {
    switch (category) {
      case 'bedroom':
        return 'Room: Bedroom';
      case 'bathroom':
        return 'Room: Bathroom';
      case 'dining':
        return 'Room: Dining Hall';
      case 'medical':
        return 'Room: Medical Wing';
      case 'recreation':
        return 'Room: Recreation';
      case 'utility':
        return 'Room: Utility/Support';
      default:
        return 'Room: Unassigned';
    }
  }

  private formatCount(count: number, noun: string): string {
    const pluralized = count === 1 ? noun : `${noun}s`;
    return `${count} ${pluralized}`;
  }

  private getInstructionText(mode: PlacementMode): string {
    if (mode === 'floor') {
      return 'Drag to paint floor tiles. Hotkeys: 1:Floors, 2:Walls, 3:Furniture, 4:Doors, 5:Room Manager. F:Furniture UI, Q:Quality, P:Spawn resident.';
    }

    if (mode === 'door') {
      return 'Click to place doors on walls. Press R to rotate door direction. Doors automatically orient based on selected direction. Hotkeys: 1:Floors, 2:Walls, 3:Furniture, 4:Doors, 5:Room Manager.';
    }

    if (mode === 'room_manager') {
      return 'Use the Room Manager panel to categorize rooms by type. Click on room category buttons to assign room types. Hotkeys: 1:Floors, 2:Walls, 3:Furniture, 4:Doors.';
    }

    if (mode === 'furniture') {
      return 'Click to place furniture. Select from Furniture panel (F) or use F:Furniture UI, R:Rotate. P:Spawn resident.';
    }

    return 'Drag to build walls. Use the Wall Mode button to switch between Room Walls (perimeter) and Single Wall (individual lines). Hotkeys: 1:Floors, 2:Walls, 3:Furniture, 4:Doors, 5:Room Manager. F:Furniture UI, P:Spawn resident.';
  }

  private getHistoryPrefix(source: HistorySource): string {
    if (source === 'undo') {
      return 'Undo: ';
    }
    if (source === 'redo') {
      return 'Redo: ';
    }
    return '';
  }

  private getPlacementVerb(source: HistorySource): string {
    if (source === 'undo') {
      return 'Restored';
    }
    if (source === 'redo') {
      return 'Reapplied';
    }
    return 'Placed';
  }

  private getRemovalVerb(source: HistorySource): string {
    return 'Removed';
  }

  public toggleRoomUI(): void {
    this.roomUI.toggle();
  }

  public showRoomUI(): void {
    this.roomUI.show();
  }

  public hideRoomUI(): void {
    this.roomUI.hide();
  }

  public updateSelectedRoom(room: RoomDefinition | null): void {
    this.roomUI.updateSelectedRoom(room);
  }

  public getRoomUI(): RoomUI {
    return this.roomUI;
  }

  public getFurnitureUI(): FurnitureUI {
    return this.furnitureUI;
  }

  public toggleFurnitureUI(): void {
    this.furnitureUI.toggle();
  }

  public showFurnitureUI(): void {
    this.furnitureUI.show();
  }

  public hideFurnitureUI(): void {
    this.furnitureUI.hide();
  }

  public updateControlsText(text: string): void {
    this.controlsLabel.text = text;
  }

  public exitRoomCategorizationMode(): void {
    this.roomUI.exitCategorizationMode();
  }

  public setWallModeToggleCallback(callback: () => WallBuildMode): void {
    this.wallModeToggleCallback = callback;
  }

  public updateWallModeButton(mode: WallBuildMode): void {
    this.wallModeLabel.text = mode === 'rectangle' ? 'Room Walls' : 'Single Wall';
    
    // Update icon based on mode
    this.wallModeIcon.clear();
    if (mode === 'rectangle') {
      // Draw rectangle icon
      this.wallModeIcon.lineStyle(2, 0xffffff, 1);
      this.wallModeIcon.drawRect(2, 2, 16, 12);
    } else {
      // Draw single line icon
      this.wallModeIcon.lineStyle(3, 0xffffff, 1);
      this.wallModeIcon.moveTo(2, 8);
      this.wallModeIcon.lineTo(18, 8);
    }
  }

  private createWallModeButton(): void {
    this.wallModeButton = new Container();
    
    // Create button background
    const buttonBg = new Graphics();
    buttonBg.beginFill(0x333333, 0.8);
    buttonBg.lineStyle(2, 0x666666, 1);
    buttonBg.drawRoundedRect(0, 0, 120, 32, 4);
    buttonBg.endFill();
    this.wallModeButton.addChild(buttonBg);
    
    // Create icon
    this.wallModeIcon = new Graphics();
    this.wallModeIcon.position.set(8, 8);
    this.wallModeButton.addChild(this.wallModeIcon);
    
    // Create label
    const labelStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 12,
      fill: 0xffffff,
      stroke: '#000000',
      strokeThickness: 2
    });
    
    this.wallModeLabel = new Text('Room Walls', labelStyle);
    this.wallModeLabel.position.set(32, 8);
    this.wallModeButton.addChild(this.wallModeLabel);
    
    // Position button in top-left area, below the other UI elements
    this.wallModeButton.position.set(16, 140);
    
    // Make button interactive
    this.wallModeButton.eventMode = 'static';
    this.wallModeButton.cursor = 'pointer';
    this.wallModeButton.on('pointerdown', this.onWallModeButtonClick.bind(this));
    
    // Start hidden - will be shown when in wall mode
    this.wallModeButton.visible = false;
    
    // Initialize the icon
    this.updateWallModeButton('rectangle');
    
    this.addChild(this.wallModeButton);
  }

  private onWallModeButtonClick(event: FederatedPointerEvent): void {
    if (this.wallModeToggleCallback) {
      // The callback should return the new mode after toggling
      const newMode = this.wallModeToggleCallback();
      this.updateWallModeButton(newMode);
    }
  }
}
